import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import api, { setMemToken } from '../lib/apiClient';
import { queryClient } from '../lib/queryClient';
import { isTossitNativeApp, getNativeAuthToken, nativeLogout } from '../utils/nativeBridge';
import { registerFcmToken } from '../utils/fcm';
import { trackEvent, maskPhone, EVENTS } from '../utils/analytics';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);

    // Validate a token against /auth/me and set state.
    // Returns { ok, authError } — authError is true only on a genuine 401/403
    // so transient network/5xx failures don't wipe a still-valid token.
    const applyToken = async (t) => {
        if (!t) return { ok: false, authError: false };
        try {
            const response = await axios.get(`${API_URL}/api/auth/me`, {
                headers: { Authorization: `Bearer ${t}` }
            });
            localStorage.setItem('token', t);
            setMemToken('token', t); // keep an in-memory copy for the WebView (localStorage can be flaky)
            setToken(t);
            setUser(response.data);
            registerFcmToken(t);
            return { ok: true, authError: false };
        } catch (error) {
            const status = error?.response?.status;
            return { ok: false, authError: status === 401 || status === 403 };
        }
    };

    useEffect(() => {
        // The customer auth bootstrap must NOT run on cook/admin routes: inside the
        // native WebView getNativeAuthToken() returns whatever session the host holds
        // (e.g. the COOK token), and validating that against the customer /auth/me
        // fails → it would wrongly fire nativeLogout() and log the cook out.
        const p = (typeof window !== 'undefined' && window.location.pathname) || '';
        const onCustomerRoute = !p.startsWith('/cook') && !p.startsWith('/admin');

        const initAuth = async () => {
            if (!onCustomerRoute) { setLoading(false); return; }
            let savedToken = localStorage.getItem('token');
            // Inside the native WebView, localStorage may not persist across tab/
            // navigation reloads — so if we have no local token, ask the host for it.
            if (!savedToken && isTossitNativeApp()) {
                savedToken = await getNativeAuthToken();
            }
            if (savedToken) {
                const { ok, authError } = await applyToken(savedToken);
                // Only clear the token + hand off to the host on a real auth failure.
                // Transient network/5xx errors keep the token so a retry can recover.
                if (!ok && authError) {
                    localStorage.removeItem('token');
                    setMemToken('token', null);
                    setToken(null);
                    setUser(null);
                    // Never show the web login inside the app — let the host handle it.
                    if (isTossitNativeApp()) nativeLogout();
                }
            }
            setLoading(false);
        };
        initAuth();

        // Allow the native host to push / refresh a token at any time
        // (e.g. right after loading a new tab). Host calls: window.tossitSetAuthToken(token)
        window.tossitSetAuthToken = (newToken) => { applyToken(newToken); };
        return () => { try { delete window.tossitSetAuthToken; } catch (e) { window.tossitSetAuthToken = undefined; } };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const sendOTP = async (phone) => {
        trackEvent(EVENTS.PHONE_NUMBER_SUBMIT, { phone_last4: maskPhone(phone) });
        const response = await api.post('/auth/send-otp', { phone });
        return response.data;
    };

    const verifyOTP = async (phone, otp, name = null, referralCode = null) => {
        const payload = { phone, otp };
        if (name) payload.name = name;
        if (referralCode) payload.referral_code = referralCode;

        let response;
        try {
            response = await api.post('/auth/verify-otp', payload);
        } catch (e) {
            if (!name) trackEvent(EVENTS.OTP_SUBMIT, { otp_status: 'failed' });
            throw e;
        }
        const data = response.data;
        // otp_submit only for the OTP-entry call; the name-completion call fires name_submit.
        if (name) trackEvent(EVENTS.NAME_SUBMIT, {});
        else trackEvent(EVENTS.OTP_SUBMIT, { otp_status: 'success' });

        // Check if name is needed (new user without name)
        if (data.needs_name) {
            return { needs_name: true, message: data.message };
        }
        
        const { token: newToken, user: userData, is_new_user } = data;
        
        localStorage.setItem('token', newToken);
        setMemToken('token', newToken);
        setToken(newToken);
        setUser(userData);
        registerFcmToken(newToken);

        // GA4: sign_up for a brand-new user, login for a returning one. customer_id only (no PII).
        trackEvent(is_new_user ? EVENTS.SIGN_UP : EVENTS.LOGIN, { method: 'phone', customer_id: userData?.id });
        
        return { user: userData, is_new_user, token: newToken };
    };

    const logout = () => {
        localStorage.clear();
        setMemToken('token', null);
        setToken(null);
        setUser(null);
        queryClient.clear(); // drop all cached API responses for the previous user
        // In the native WebView, hand off to the host instead of showing web login.
        if (isTossitNativeApp()) nativeLogout();
    };

    return (
        <AuthContext.Provider value={{ user, token, loading, sendOTP, verifyOTP, logout }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
